/** 接笔救援协议。游戏时间使用固定 60 Hz tick，与绘画猜词的毫秒笔迹协议隔离。 */
export const RESCUE_VERSION = 2;
export const TICK_RATE = 60;
export const CAMPAIGN_LENGTH = 48;
export const RESCUE_LIMITS = { events: 96, strokes: 32, pointsPerStroke: 64, points: 1024, ticks: 1800 };
export type RescueRole = 'lead' | 'follow';
export type Material = 'support' | 'spring';
export interface Vec { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }
export interface RescueStroke { id: string; material: Material; points: Vec[] }
export type RescueEvent = { tick: number; kind: 'add'; stroke: RescueStroke } | { tick: number; kind: 'erase'; strokeId: string };
export interface Recording { version: 1; events: RescueEvent[] }
export interface BallSpec { id: string; position: Vec; radius: number; velocity?: Vec; density?: number; fragile?: number; color: string; helper?: boolean }
export interface GoalSpec extends Rect { id: string; ball: string; label: string }
export interface RailSpec { id: string; points: Vec[]; material?: Material }
export interface SwitchSpec extends Rect { id: string; ball?: string; hold: number }
export interface GateSpec extends Rect { id: string; switches: string[]; all?: boolean; delay?: number }
export interface LeverSpec { id: string; center: Vec; width: number; angle?: number; threshold: number; direction: 1 | -1; limit: number }
export interface MotionSpec extends Rect { id: string; travel: Vec; period: number; phase?: number; kind: 'platform' | 'shutter' }
export interface WindSpec extends Rect { id: string; force: Vec; period?: number; on?: number; phase?: number }
export interface PulseSpec extends Rect { id: string; period: number; on: number; phase: number }
export interface RescueLevel {
  id: string; version: 1 | 2; number: number; chapter: string; title: string;
  objective: string; leadTask: string; followTask: string; tip: string;
  ticks: number; ink: Record<RescueRole, number>; areas: Record<RescueRole, Rect[]>;
  materials: Material[]; balls: BallSpec[]; rails: RailSpec[]; goals: GoalSpec[];
  checkpoints: (Rect & { ball: string })[]; switches: SwitchSpec[]; gates: GateSpec[]; levers: LeverSpec[];
  hazards: Rect[]; requiredSwitches: string[]; requiredLevers: string[]; requiredSprings: number;
  requiredErase: boolean; simultaneous?: boolean;
  motions?: MotionSpec[]; winds?: WindSpec[]; pulses?: PulseSpec[];
  theme?: 'garden' | 'workshop' | 'sky' | 'night'; layout?: string;
  seed?: number; generatorVersion?: number; fingerprint: string;
}
export interface ReferencePair { lead: Recording; follow: Recording; name: string }
export interface AuthoredLevel { level: RescueLevel; references: [ReferencePair, ReferencePair] }
export interface BallFrame extends Vec { id: string; radius: number; color: string; broken: boolean; rescued: boolean; helper: boolean; fragile: boolean }
export interface StrokeFrame extends RescueStroke { role: RescueRole }
export interface SimulationFrame {
  tick: number; balls: BallFrame[]; strokes: StrokeFrame[];
  gates: { id: string; open: boolean }[]; levers: { id: string; angle: number }[];
  switches: string[]; leadPassed: boolean; success: boolean; failed: boolean;
  motions?: (Rect & { id: string })[]; pulses?: { id: string; active: boolean }[];
  objectives?: { label: string; complete: boolean }[];
  message: string; contributions: Record<RescueRole, boolean>; springHits: number; erased: boolean;
}
export interface SimulationResult extends SimulationFrame { ink: Record<RescueRole, number> }
export type RescuePhase = 'lead' | 'lead-review' | 'follow' | 'follow-review' | 'cleared';
export interface RescueRoundSummary {
  id: string; ordinal: number; levelId: string; title: string; chapter: string; phase: RescuePhase;
  revision: number; leadId: string; followId?: string; createdAt: number; clearedAt?: number; reason?: string;
  hasReplay?: boolean;
}
export interface RescueProgress { completed: number; endlessCompleted: number; campaignLength?: number }
export interface RescueView {
  mode: 'relay-rescue'; id: string; status: 'inviting' | 'active' | 'closed';
  members: { id: string; nickname: string; avatarUrl?: string }[];
  inviteCode?: string; inviteExpiresAt?: number; updatedAt: number;
  progress: RescueProgress; round: RescueRoundSummary; level: RescueLevel; history: RescueRoundSummary[];
}
export interface RescueReplay { round: RescueRoundSummary; level: RescueLevel; lead: Recording; follow: Recording }
export const emptyRecording = (): Recording => ({ version: 1, events: [] });
/** 协议数据只含 JSON 值；微信小游戏运行时不依赖 structuredClone。 */
export const copyRecording = (recording: Recording): Recording => JSON.parse(JSON.stringify(recording));
export const inside = (p: Vec, r: Rect, margin = 0) => p.x >= r.x - margin && p.x <= r.x + r.w + margin && p.y >= r.y - margin && p.y <= r.y + r.h + margin;
export const lineLength = (points: Vec[]) => points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - points[i].x, p.y - points[i].y), 0);
